import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { get } from "@/lib/api";
import { qs } from "@/lib/crm";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * Searchable picker for CRM records (contacts, companies, leads, opportunities) fed by the paginated API.
 * `endpoint` returns { items } ; `labelOf(item)` renders the option; `value` is the selected id.
 */
export const EntityCombobox = ({ endpoint, labelOf, value, onChange, placeholder, params = {}, testId = "entity-combobox", disabled = false, allowClear = true, initialLabel }) => {
  const { t } = useTranslation();
  const { orgId } = useAuth();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const id = setTimeout(() => setDebounced(q), 250);
    return () => clearTimeout(id);
  }, [q]);
  const { data, isLoading } = useQuery({ queryKey: ["combobox", endpoint, orgId, debounced, JSON.stringify(params)], queryFn: () => get(`${endpoint}${qs({ q: debounced, page_size: 20, ...params })}`), enabled: !!orgId && open });
  const items = useMemo(() => data?.items || [], [data]);
  const selected = items.find((i) => i.id === value);
  const label = selected ? labelOf(selected) : value ? initialLabel || "…" : null;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" aria-expanded={open} disabled={disabled} className={cn("w-full justify-between bg-card font-normal", !label && "text-muted-foreground")} data-testid={testId}>
          <span className="truncate">{label || placeholder || t("common.select")}</span>
          <span className="ml-2 flex shrink-0 items-center gap-1">
            {allowClear && value && <X className="h-3.5 w-3.5 opacity-60 hover:opacity-100" onClick={(e) => { e.stopPropagation(); onChange(null, null); }} data-testid={`${testId}-clear`} />}
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] min-w-[260px] bg-card p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder={t("common.searchPlaceholder")} value={q} onValueChange={setQ} data-testid={`${testId}-search`} />
          <CommandList>
            <CommandEmpty>{isLoading ? t("common.loading") : t("common.noResults")}</CommandEmpty>
            <CommandGroup>
              {items.map((item) => (
                <CommandItem key={item.id} value={item.id} onSelect={() => { onChange(item.id, item); setOpen(false); }} data-testid={`${testId}-option`}>
                  <Check className={cn("mr-2 h-4 w-4", value === item.id ? "opacity-100" : "opacity-0")} />
                  <span className="truncate">{labelOf(item)}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export const contactLabel = (c) => `${c.first_name || ""} ${c.last_name || ""}`.trim() + (c.company_name ? ` · ${c.company_name}` : "");
export const companyLabel = (c) => c.name;
export const leadLabel = (l) => l.title;
export const opportunityLabel = (o) => o.name;
